import join from 'url-join'

import { createAction, http, asyncAction } from '../util'
import config from '../config'
import { selectADUserInfo, selectJiraUser } from '../auth/reducer'
import { WikiMarkupTransformer } from '@atlaskit/editor-wikimarkup-transformer';

export let setRequestType = createAction('SET_REQUEST_TYPE')
export let setIssue = createAction('SET_ISSUE')
export let setIssueError = createAction('SET_ISSUE_ERROR')
export let setEditComment = createAction('SET_EDIT_COMMENT')
export let setEditingComment = createAction('SET_EDITING_COMMENT')
export let setComment = createAction('SET_COMMENT')
export let updateIssueData = createAction('UPDATE_ISSUE_DATA')
export let clearNewIssueKey = createAction('CLEAR_NEW_ISSUE_KEY', {noPayload: true})
export let clearIssues = createAction('CLEAR_ISSUES',{noPayload: true})

const api = join.bind(null, config.apiUrl)
const transformer = new WikiMarkupTransformer()

export const getAllRequestTypes = asyncAction({
    baseName: 'REQUEST_TYPES_GET_ALL',
    action: action => () => (dispatch, getState) => {
        // Use cached version if already loaded
        let requestTypes = getState().serviceDesk.get('requestTypes')
        if (requestTypes.size === 0 ) {
            dispatch(action.start())
            return http.get(api(`/ssp/requests`))
            .then(searchResults => {
                dispatch(action.success(searchResults))
            }).catch(err => {
                console.error('error getting Request Types api response', err)
                dispatch(action.error(err))
            })
        } else {
            return Promise.resolve()
        }
    }
})

export const getRequestType = asyncAction({
    baseName: 'REQUEST_TYPE_GET',
    action: action => (requestTypeName) => (dispatch) => {
        dispatch(action.start())
        return http.get(api(`/ssp/requests/${requestTypeName}`))
        .then(searchResults => {
            dispatch(action.success(searchResults))
        }).catch(err => {
            console.error('error getting Request Type api response', err)
            dispatch(action.error(err))
        })
    }
})

export const getAllUserIssues = asyncAction({
    baseName: 'USER_ISSUES_GET_ALL',
    action: action => (refresh, startAt) => (dispatch, getState) => {
        let state = getState()        
        let userIssues = state.serviceDesk.get('issues')
        if (userIssues.size === 0 || refresh || startAt > userIssues.length ) {
            let userInfo = selectADUserInfo(state)
            let quarry = `(assignee=${userInfo.username} or reporter=${userInfo.username} or "Requestor Email Address" ~ '${userInfo.email}') and project=baat and type=request and "Request Type" is not Empty ORDER BY created DESC`        
            dispatch(action.start())
            return http.get(api(`/ssp/issues?query=${quarry}&maxResults=10&startAt=${startAt - 1}`))
            .then(searchResults => {
                dispatch(action.success({userIssues, searchResults, refresh}))
            }).catch(err => {
                console.error('error getting User Issues api response', err)
                dispatch(action.error(err))
            })
        } else {
            return Promise.resolve()
        }
    }
})

export const getIssue = asyncAction({
    baseName: 'ISSUE_GET',
    action: action => (issueKey) => (dispatch) => {
        dispatch(action.start())
        return http.get(api(`/ssp/issues?key=${issueKey}`))
        .then(searchResults => {
            dispatch(action.success(searchResults))
        }).catch(error => {
            let message = error.message
            if (message) {
                dispatch(setIssueError(message))
            }            
            console.error('error getting Issue api response', error)
            dispatch(action.error(error))
        })
    }
})

export const createIssue = asyncAction({
    baseName: 'ISSUE_CREATE',
    action: action => (formData, requestType) => (dispatch, getState) => {
        formData = processWikiFields(formData, requestType)
        let issueCount = getState().serviceDesk.get('totalIssues')
        let issues = getState().serviceDesk.get('issues')
        dispatch(action.start(formData))
        return http.post(api(`/ssp/issues`), formData)
        .then(newIssue => {
            dispatch(action.success({newIssue, issues, issueCount}))
        }).then(() => dispatch(getAllUserIssues()))
        .catch(err => {
            console.error('error with create issue api', err)
            dispatch(action.error(err))
        })
    }
})

export const updateIssue = asyncAction({
    baseName: 'ISSUE_UPDATE',
    action: action => (formData, issueKey) => (dispatch, getState) => {
        formData = processWikiFields(formData, formData.requestType)
        let issues_list = getState().serviceDesk.get('issues')
        dispatch(action.start(formData))
        return http.put(api(`/ssp/issues?key=${issueKey}`), formData)
        .then(response => {            
            dispatch(action.success({response, issues_list}))
            //dispatch(setIssue(formData))
        })
        .catch(err => {
            console.error('error with update issue api', err)
            dispatch(action.error(err))
        })
    }
})

export const getIssueComments = asyncAction({
    baseName: 'COMMENTS_GET',
    action: action => (issueKey) => (dispatch, getState) => {                
        let user = selectADUserInfo(getState())
        let jiraUser = selectJiraUser(getState())
        dispatch(action.start(issueKey))
        return http.get(api(`/ssp/comments?key=${issueKey}`))
        .then(issueComments => {
            dispatch(action.success({issueComments, user, jiraUser}))
        })
        .catch(err => {
            console.error('error with update issue api', err)
            dispatch(action.error(err))
        })
    }
})

export const updateIssueComment = asyncAction({
    baseName: 'COMMENTS_UPDATE',
    action: action => (issueKey, comment) => (dispatch, getState) => {
        comment.body = encodeWikiFieldData(comment.body)
        let commentsList = getState().serviceDesk.get('issueComments')
        let requestor = selectADUserInfo(getState())
        let jUser = selectJiraUser(getState())                
        dispatch(action.start(issueKey))
        return http.put(api(`/ssp/comments?key=${issueKey}&requestor=${requestor.displayName}`), comment)
        .then(updatedComment => {
            dispatch(action.success({updatedComment, commentsList, requestor, jUser}))
        })
        .catch(err => {
            console.error('error with update issue api', err)
            dispatch(action.error(err))
        })
    }
})

export const addIssueComment = asyncAction({
    baseName: 'COMMENTS_ADD',
    action: action => (issueKey, commentBody) => (dispatch, getState) => {
        commentBody = encodeWikiFieldData(commentBody)
        let comments = getState().serviceDesk.get('issueComments')
        let commentAuthor = selectADUserInfo(getState())
        let JiraUser = selectJiraUser(getState())
        dispatch(action.start(issueKey))
        return http.post(api(`/ssp/comments?key=${issueKey}&requestor=${commentAuthor.displayName}`), commentBody)
        .then(newComment => {
            dispatch(action.success({newComment, comments, commentAuthor, JiraUser}))
        })
        .catch(err => {
            console.error('error with update issue api', err)
            dispatch(action.error(err))
        })
    }
})

function processWikiFields(formData, requestType) {
    requestType.inputs.properties.map(field => {
        if (field.type == 'wiki' || field.jiraFieldName =='description') {
            formData[field.id] = encodeWikiFieldData(formData[field.id])
        }
    })
    
    return formData
}

function encodeWikiFieldData(wikiData) {
    return transformer.encode(wikiData)
}


// WEBPACK FOOTER //
// ./client/src/serviceDesk/actions.js